import axios from "axios";

// On a client-facing host (e.g. proposals.powerrayllc.com) every call stays on that same address: no GridForge host in any request.
const _onPublicHost = (() => {
  try {
    const here = window.location.hostname.toLowerCase();
    const gf = new URL(process.env.REACT_APP_BACKEND_URL).hostname.toLowerCase();
    return here && here !== gf && here !== "localhost" && here !== "127.0.0.1" && !/emergentagent\.com$/.test(here);
  } catch { return false; }
})();
const BACKEND_URL = _onPublicHost ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// resolve a relative backend asset path (e.g. "/api/sheets/x/image") to an absolute URL
export const assetUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// H: the one-line "why" box on the versions bar rides on every write to that bid as X-Change-Reason (logged with the lines it touched).
let _changeCtx = { bidId: null, reason: "", by: "" };
export const setChangeContext = (ctx) => { _changeCtx = { ..._changeCtx, ...ctx }; };
api.interceptors.request.use((cfg) => {
  const m = (cfg.method || "get").toLowerCase();
  if (m !== "get" && _changeCtx.bidId && _changeCtx.reason && (cfg.url || "").includes(`/bids/${_changeCtx.bidId}`)) {
    cfg.headers = cfg.headers || {};
    cfg.headers["X-Change-Reason"] = _changeCtx.reason.slice(0, 300);
    if (_changeCtx.by) cfg.headers["X-Change-By"] = _changeCtx.by.slice(0, 80);
  }
  return cfg;
});

// Transient edge/origin blips (backend mid-reload, cold start) surface as 520/502/503/504
// or a network error with no response. Retry those a few times with backoff so a brief
// hiccup never dumps a signed-in user to /login. Real errors (4xx) are never retried.
const TRANSIENT = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config;
    if (!cfg || cfg._retryDisabled) return Promise.reject(error);
    const status = error.response?.status;
    if (status === 403 && error.response?.data?.detail === "mfa_setup_required" && !window.location.pathname.startsWith("/settings")) {
      window.location.href = "/settings#mfa";
      return Promise.reject(error);
    }
    const isTransient = status ? TRANSIENT.has(status) : (error.code !== "ERR_CANCELED" && !error.response);
    if (!isTransient) return Promise.reject(error);
    cfg._retryCount = (cfg._retryCount || 0) + 1;
    if (cfg._retryCount > 4) return Promise.reject(error);
    await sleep(Math.min(400 * 2 ** (cfg._retryCount - 1), 3000));
    return api(cfg);
  }
);

export default api;
